'use client';

import { cn } from '@/app/components/lib/utils';
import * as React from 'react';
const ToggleGroupContext = React.createContext(null);
function useToggleGroupContext() {
  const context = React.useContext(ToggleGroupContext);
  if (!context) {
    throw new Error('ToggleGroupItem must be used within ToggleGroup');
  }
  return context;
}
function ToggleGroupRoot({
  value,
  onValueChange,
  disabled = false,
  className,
  children,
  ...props
}) {
  const contextValue = React.useMemo(() => ({
    value,
    onValueChange: onValueChange,
    disabled
  }), [value, onValueChange, disabled]);
  return <ToggleGroupContext.Provider value={contextValue}>
            <div role="radiogroup" aria-disabled={disabled || undefined} className={cn('inline-flex items-center gap-0.5 rounded-lg border border-grey-100 bg-muted p-0.75', className)} {...props}>
                {children}
            </div>
        </ToggleGroupContext.Provider>;
}
const ToggleGroupItem = React.forwardRef(({
  value,
  className,
  disabled,
  children,
  onClick,
  ...props
}, ref) => {
  const {
    value: selectedValue,
    onValueChange,
    disabled: groupDisabled
  } = useToggleGroupContext();
  const isSelected = selectedValue === value;
  const isDisabled = groupDisabled || disabled;
  return <button ref={ref} type="button" role="radio" aria-checked={isSelected} disabled={isDisabled} data-state={isSelected ? 'on' : 'off'} className={cn('inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:ring-offset-1', 'disabled:pointer-events-none disabled:opacity-50', isSelected ? 'border border-blue-300 bg-page text-brand shadow' : 'border border-transparent bg-transparent text-foreground-subtle hover:text-foreground-muted', 'hover:cursor-pointer', className)} onClick={event => {
    onClick?.(event);
    if (event.defaultPrevented || isDisabled || isSelected) {
      return;
    }
    onValueChange(value);
  }} {...props}>
            {children}
        </button>;
});
ToggleGroupItem.displayName = 'ToggleGroupItem';
function ToggleGroupButton({
  className,
  disabled,
  children,
  onClick,
  ...props
}) {
  return <button type="button" role="radio" className={cn('inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:ring-offset-1', 'disabled:pointer-events-none disabled:opacity-50', 'border border-blue-300 bg-page text-brand shadow', 'hover:cursor-pointer', className)} onClick={event => {
    onClick?.(event);
    if (event.defaultPrevented || disabled) {
      return;
    }
  }} {...props}>
            {children}
        </button>;
}
export { ToggleGroupRoot as ToggleGroup, ToggleGroupItem, ToggleGroupButton };